// OWNED BY the calls implementation agent (see ../CallsTab.tsx ownership note).
//
// React binding for the contact's call list: renders instantly from the session
// cache (warmed by preloadCallsTab on contacts-table row hover / page mount),
// then keeps the list LIVE via per-virtual-number onSnapshot listeners that
// write back into the same cache key (so the tab-strip badge updates too).
// The tab stays mounted after first visit — the listeners are registered once
// per contact id and cleaned up when the page unmounts.

import { useEffect } from "react";
import { useSessionCacheValue } from "../../data/sessionCache";
import {
  CALLS_MAX_AGE_MS,
  contactCallsKey,
  fetchCallsForContact,
  subscribeToContactCalls
} from "./callsData";
import type { CallSummary, RealEstateContact } from "../../../../../../@types/sharedSchema";

type ContactCallsState = {
  calls: CallSummary[] | undefined;
  loading: boolean;
  error: Error | null;
  /** Drops the cache entry and refetches — the tab's failed-load retry. */
  refresh: () => void;
};

export function useContactCalls(contact: RealEstateContact): ContactCallsState {
  const contactId = contact?.id ?? "";

  const { data, loading, error, refresh } = useSessionCacheValue<CallSummary[]>(
    contactCallsKey(contactId),
    () => fetchCallsForContact(contactId),
    { maxAgeMs: CALLS_MAX_AGE_MS }
  );

  useEffect(() => {
    if (!contactId) return undefined;
    return subscribeToContactCalls(contactId);
  }, [contactId]);

  return {
    calls: data,
    // "loading" only on a true cold start — a warm cache renders instantly.
    loading: loading && data === undefined,
    error,
    refresh
  };
}
